// Actions Queravel proposes during a conversation. Queravel only suggests. The device checks the
// arguments, works out who "to" means from its own chats, and asks the person before anything is
// written. Reads run at once and the person is told. Each outcome goes back to Queravel as an
// action_result on the next request.

import type { PeniellyItem, PeniellySearchParams } from "./penielly";
import type { ActionResult, QueravelAction } from "./queravel";

/** A proposal nobody answers within ten minutes expires. */
export const ACTION_TIMEOUT_MS = 10 * 60 * 1000;

export type ActionName = "rhema.send" | "penielly.search" | "malakh.draft";

/** A chat the device found for the "to" of a send. */
export type Recipient = { chatId: string; label: string; detail?: string };

export type SendArgs = { to: string; text?: string; attachments: string[] };
export type SearchArgs = {
  q: string;
  date_from?: string;
  date_to?: string;
  person?: string;
  place?: string;
  type?: "image" | "video";
  favorite?: boolean;
  limit: number;
};
export type DraftArgs = { to: string[]; subject: string; body: string; attachments: string[] };

export type ProposalState = "pending" | "running" | "done" | "rejected" | "failed" | "timeout";

type Base = {
  id: string;
  label: string;
  state: ProposalState;
  createdAt: number;
  /** Why it failed, in words the person can read. */
  error?: string;
  /** A line to show for reads that ran without asking, such as "Queravel searched your photos". */
  notice?: string;
};

export type Proposal =
  | (Base & {
      name: "rhema.send";
      write: true;
      args: SendArgs;
      /** Chats that match "to". The person picks when there is more than one. */
      recipients: Recipient[];
      chosen: Recipient | null;
      photos: PeniellyItem[];
    })
  | (Base & { name: "penielly.search"; write: false; args: SearchArgs })
  | (Base & { name: "malakh.draft"; write: true; args: DraftArgs; photos: PeniellyItem[] })
  /** An action that failed its checks, or one this app does not know. */
  | (Base & { name: "other"; action: string; write: false; args: Record<string, unknown> });

/** What the action engine needs from the app. */
export type ActionHost = {
  /** Chats and contacts on this device that match a name, best match first. */
  findRecipients(to: string): Promise<Recipient[]> | Recipient[];
  /** Sends text and photos to a chat as ordinary encrypted Rhema messages. */
  sendMessage(chatId: string, message: { text?: string; photos: PeniellyItem[] }): Promise<void>;
  searchPhotos(params: PeniellySearchParams): Promise<{ items: PeniellyItem[]; total?: number }>;
  /** Opens a Malakh draft. Never sends it. */
  draftEmail(draft: { to: string[]; subject: string; body: string; photos: PeniellyItem[] }): Promise<void>;
};

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;

class Invalid extends Error {}

function str(args: Record<string, unknown>, key: string, opts: { required?: boolean; max?: number } = {}) {
  const v = args[key];
  if (v === undefined || v === null) {
    if (opts.required) throw new Invalid(`${key} is required`);
    return undefined;
  }
  if (typeof v !== "string") throw new Invalid(`${key} must be a string`);
  if (opts.max && v.length > opts.max) throw new Invalid(`${key} is too long`);
  if (opts.required && !v.trim()) throw new Invalid(`${key} is required`);
  return v;
}

function refs(args: Record<string, unknown>, max: number): string[] {
  const v = args.attachments;
  if (v === undefined || v === null) return [];
  if (!Array.isArray(v) || v.some((x) => typeof x !== "string"))
    throw new Invalid("attachments must be refs");
  if (v.length > max) throw new Invalid(`at most ${max} attachments`);
  return v as string[];
}

function date(args: Record<string, unknown>, key: string): string | undefined {
  const v = str(args, key);
  if (v !== undefined && !DATE.test(v)) throw new Invalid(`${key} must be YYYY-MM-DD`);
  return v || undefined;
}

export function validateSend(args: Record<string, unknown>): SendArgs {
  const to = str(args, "to", { required: true, max: 200 }) as string;
  const text = str(args, "text", { max: 4000 });
  const attachments = refs(args, 10);
  if (!text?.trim() && !attachments.length) throw new Invalid("text or attachments is required");
  return { to: to.trim(), ...(text?.trim() ? { text } : {}), attachments };
}

// Queravel may ask for up to 200, but at most this many items go back into its context.
export const SEARCH_RESULT_MAX = 50;

export function validateSearch(args: Record<string, unknown>): SearchArgs {
  // Queravel sends `query`; `q` is the earlier spelling. `person_id` is ignored on purpose,
  // because Queravel never holds Penielly ids.
  const q = str(args, "query", { max: 500 }) ?? str(args, "q", { max: 500 }) ?? "";
  const limitRaw = args.limit ?? 20;
  if (typeof limitRaw !== "number" || !Number.isInteger(limitRaw) || limitRaw < 1 || limitRaw > 200)
    throw new Invalid("limit must be a whole number from 1 to 200");
  const limit = Math.min(limitRaw, SEARCH_RESULT_MAX);
  const favorite = args.favorite;
  if (favorite !== undefined && typeof favorite !== "boolean")
    throw new Invalid("favorite must be true or false");
  const typeRaw = str(args, "type");
  let type: SearchArgs["type"];
  if (typeRaw === "photo" || typeRaw === "image") type = "image";
  else if (typeRaw === "video") type = "video";
  else if (typeRaw) throw new Invalid("type must be photo or video");
  const out: SearchArgs = { q, limit };
  if (favorite) out.favorite = true;
  const df = date(args, "date_from");
  const dt = date(args, "date_to");
  const person = str(args, "person", { max: 200 });
  const place = str(args, "place", { max: 200 });
  if (df) out.date_from = df;
  if (dt) out.date_to = dt;
  if (person?.trim()) out.person = person.trim();
  if (place?.trim()) out.place = place.trim();
  if (type) out.type = type;
  return out;
}

export function validateDraft(args: Record<string, unknown>): DraftArgs {
  const to = args.to;
  if (!Array.isArray(to) || !to.length || to.length > 50) throw new Invalid("to must list 1 to 50 addresses");
  for (const a of to)
    if (typeof a !== "string" || !EMAIL.test(a.trim())) throw new Invalid("to has a bad address");
  const subject = str(args, "subject", { max: 500 }) ?? "";
  const body = str(args, "body", { max: 100_000 }) ?? "";
  return { to: (to as string[]).map((a) => a.trim()), subject, body, attachments: refs(args, 20) };
}

/** Runs the actions for one Queravel conversation. Photo refs only mean something inside it. */
export class ActionEngine {
  private proposals = new Map<string, Proposal>();
  private order: string[] = [];
  private delivered = new Set<string>();
  private refs = new Map<string, PeniellyItem>();
  private refOf = new Map<string, string>();
  private listeners = new Set<() => void>();
  private readonly now: () => number;

  constructor(
    private readonly host: ActionHost,
    opts: { now?: () => number } = {},
  ) {
    this.now = opts.now ?? (() => Date.now());
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private changed() {
    for (const l of this.listeners) l();
  }

  list(): Proposal[] {
    this.expire();
    return this.order.map((id) => this.proposals.get(id) as Proposal);
  }

  get(id: string): Proposal | undefined {
    this.expire();
    return this.proposals.get(id);
  }

  /** An opaque handle for a photo. Queravel sees this, never Penielly's id or URL. */
  refFor(item: PeniellyItem): string {
    const known = this.refOf.get(item.id);
    if (known) return known;
    const ref = `photo-${this.refs.size + 1}`;
    this.refs.set(ref, item);
    this.refOf.set(item.id, ref);
    return ref;
  }

  photo(ref: string): PeniellyItem | undefined {
    return this.refs.get(ref);
  }

  private photosFor(list: string[]): PeniellyItem[] {
    return list.map((ref) => {
      const item = this.refs.get(ref);
      if (!item) throw new Invalid(`unknown attachment ${ref}`);
      return item;
    });
  }

  private put(p: Proposal) {
    if (!this.proposals.has(p.id)) this.order.push(p.id);
    this.proposals.set(p.id, p);
    this.changed();
  }

  private patch(id: string, patch: Partial<Base> & { chosen?: Recipient | null }) {
    const p = this.proposals.get(id);
    if (!p) return;
    this.proposals.set(id, { ...p, ...patch } as Proposal);
    this.changed();
  }

  /** Takes a proposal from the stream. Reads run straight away. Writes wait for confirm(). */
  async propose(action: QueravelAction): Promise<Proposal> {
    const base = { id: action.id, label: action.label, createdAt: this.now() };
    try {
      if (action.name === "rhema.send") {
        const args = validateSend(action.args);
        const photos = this.photosFor(args.attachments);
        const recipients = [...(await this.host.findRecipients(args.to))];
        if (!recipients.length) throw new Invalid(`no chat on this device matches ${args.to}`);
        this.put({
          ...base,
          name: "rhema.send",
          write: true,
          args,
          photos,
          recipients,
          chosen: recipients.length === 1 ? (recipients[0] as Recipient) : null,
          state: "pending",
        });
      } else if (action.name === "malakh.draft") {
        const args = validateDraft(action.args);
        const photos = this.photosFor(args.attachments);
        this.put({ ...base, name: "malakh.draft", write: true, args, photos, state: "pending" });
      } else if (action.name === "penielly.search") {
        const args = validateSearch(action.args);
        this.put({
          ...base,
          name: "penielly.search",
          write: false,
          args,
          state: "running",
          notice: "Queravel searched your photos",
        });
        await this.runSearch(action.id, args);
      } else {
        throw new Invalid(`unknown action ${action.name}`);
      }
    } catch (err) {
      const error = err instanceof Invalid ? err.message : "the action could not run";
      if (this.proposals.has(action.id)) this.patch(action.id, { state: "failed", error });
      else
        this.put({
          ...base,
          name: "other",
          action: action.name,
          write: false,
          args: action.args,
          state: "failed",
          error,
        });
    }
    return this.proposals.get(action.id) as Proposal;
  }

  private results = new Map<string, ActionResult>();

  private finish(id: string, result: ActionResult, error?: string) {
    this.results.set(id, result);
    this.patch(id, { state: result.status, ...(error ? { error } : {}) });
  }

  private async runSearch(id: string, args: SearchArgs) {
    const q = [args.q, args.person].filter(Boolean).join(" ");
    try {
      const res = await this.host.searchPhotos({
        q,
        ...(args.place ? { place: args.place } : {}),
        ...(args.date_from ? { date_from: args.date_from } : {}),
        ...(args.date_to ? { date_to: args.date_to } : {}),
        ...(args.type ? { type: args.type } : {}),
        ...(args.favorite ? { favorite: true } : {}),
        limit: args.limit,
      });
      const items = res.items.slice(0, args.limit).map((item) => ({
        ref: this.refFor(item),
        taken_at: item.captured_at,
        place: item.place,
        type: item.kind === "video" ? "video" : "photo",
      }));
      this.finish(id, { id, status: "done", result: { items, total: res.total ?? res.items.length } });
    } catch {
      this.finish(id, { id, status: "failed", error: "photo search failed" }, "photo search failed");
    }
  }

  /** Picks which chat a send goes to, when more than one matched. */
  choose(id: string, chatId: string): void {
    const p = this.get(id);
    if (p?.name !== "rhema.send" || p.state !== "pending") return;
    const chosen = p.recipients.find((r) => r.chatId === chatId);
    if (chosen) this.patch(id, { chosen });
  }

  /** The person said yes. Runs the write and records the outcome. */
  async confirm(id: string): Promise<Proposal | undefined> {
    const p = this.get(id);
    if (!p || p.state !== "pending" || !p.write) return p;
    if (p.name === "rhema.send" && !p.chosen) return p;
    this.patch(id, { state: "running" });
    try {
      if (p.name === "rhema.send" && p.chosen) {
        await this.host.sendMessage(p.chosen.chatId, {
          ...(p.args.text ? { text: p.args.text } : {}),
          photos: p.photos,
        });
        this.finish(id, { id, status: "done", result: { sent: true, to_label: p.chosen.label } });
      } else if (p.name === "malakh.draft") {
        await this.host.draftEmail({
          to: p.args.to,
          subject: p.args.subject,
          body: p.args.body,
          photos: p.photos,
        });
        this.finish(id, { id, status: "done", result: { drafted: true } });
      }
    } catch (err) {
      const error = (err as Error)?.message || "the action failed";
      this.finish(id, { id, status: "failed", error }, error);
    }
    return this.proposals.get(id);
  }

  /** The person said no. */
  reject(id: string): void {
    const p = this.get(id);
    if (!p || p.state !== "pending") return;
    this.finish(id, { id, status: "rejected" });
  }

  /** Expires proposals left unanswered for ten minutes. A send that expires counts as rejected. */
  expire(): void {
    const now = this.now();
    for (const id of this.order) {
      const p = this.proposals.get(id);
      if (!p || p.state !== "pending" || now - p.createdAt < ACTION_TIMEOUT_MS) continue;
      if (p.name === "rhema.send")
        this.finish(id, { id, status: "rejected", error: "timed out" }, "timed out");
      else this.finish(id, { id, status: "timeout" });
    }
  }

  /** Outcomes not yet sent to Queravel. Each is handed out once. */
  takeResults(): ActionResult[] {
    this.expire();
    const out: ActionResult[] = [];
    for (const id of this.order) {
      if (this.delivered.has(id)) continue;
      const p = this.proposals.get(id);
      if (!p) continue;
      let result = this.results.get(id);
      if (!result && p.state === "failed") result = { id, status: "failed", error: p.error ?? "failed" };
      if (!result) continue;
      this.delivered.add(id);
      out.push(result);
    }
    return out;
  }
}
